import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ClipboardList, FileText, Megaphone, Bell, Plus, X, Edit2, Trash2, Mic, Square, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

// ─── helpers de fecha ───
function rawDateKey(value) {
    if (!value) return null;
    return String(value).slice(0, 10);
}
function rawTime(value) {
    if (!value || value.length <= 10) return null;
    return value.slice(11, 16);
}
function localDateKey(dateObj) {
    if (!dateObj || isNaN(dateObj)) return null;
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
function todayKey() { return localDateKey(new Date()); }

// ─── grabación de voz (mismo criterio de formatos que la práctica oral) ───
const AUDIO_MIME_TYPES = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
const MAX_VOICE_SECONDS = 60;
function pickAudioMimeType() {
    if (typeof MediaRecorder === "undefined") return null;
    for (const t of AUDIO_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
}

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTH_NAMES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Roles que pueden crear recordatorios
const CAN_CREATE_REMINDERS = ["profesor", "administrativo", "psicologo", "ejecutivo"];
// Roles que ven eventos de cursos (tareas/exámenes)
const SEES_COURSE_EVENTS = ["alumno", "padre"];
// Solo alumno puede llamar /assignments/mine
const SEES_OWN_ASSIGNMENTS = ["alumno"];

const EVENT_CONFIG = {
    tarea: {
        icon: ClipboardList,
        pendingColor: "bg-amber-500/20 border-amber-500/30 text-amber-400",
        submittedColor: "bg-emerald-500/20 border-emerald-500/30 text-emerald-400",
        dot: (status) => status === "pending" ? "bg-amber-500" : "bg-emerald-500",
        label: (status) => status === "pending" ? "Tarea pendiente" : "Tarea entregada",
    },
    examen: {
        icon: FileText,
        color: "bg-blue-500/20 border-blue-500/30 text-blue-400",
        dot: () => "bg-blue-500",
        label: () => "Examen",
    },
    comunicado: {
        icon: Megaphone,
        color: "bg-slate-500/20 border-slate-500/30 text-slate-400",
        dot: () => "bg-slate-500",
        label: () => "Comunicado",
    },
    recordatorio: {
        icon: Bell,
        color: "bg-purple-500/20 border-purple-500/30 text-purple-400",
        dot: () => "bg-purple-500",
        label: () => "Recordatorio",
    },
};

export default function Calendario() {
    const { user } = useAuth();
    const role = user?.role || "alumno";

    const [loading, setLoading] = useState(true);
    const [assignments, setAssignments] = useState([]);
    const [exams, setExams] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [cursor, setCursor] = useState(() => {
        const n = new Date();
        return new Date(n.getFullYear(), n.getMonth(), 1);
    });
    const [selectedDay, setSelectedDay] = useState(todayKey());
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [editingReminder, setEditingReminder] = useState(null);
    const [prefillDate, setPrefillDate] = useState(null);
    const [voiceMode, setVoiceMode] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const promises = [api.get("/reminders")];
            if (SEES_OWN_ASSIGNMENTS.includes(role)) {
                promises.push(
                    api.get("/assignments/mine"),
                    api.get("/exams"),
                    api.get("/communications"),
                );
            } else if (SEES_COURSE_EVENTS.includes(role)) {
                promises.push(
                    api.get("/exams"),
                    api.get("/communications"),
                );
            }
            const results = await Promise.all(promises);
            setReminders(results[0].data || []);
            if (SEES_OWN_ASSIGNMENTS.includes(role)) {
                setAssignments(results[1].data || []);
                setExams(results[2].data || []);
                setCommunications(results[3].data || []);
            } else if (SEES_COURSE_EVENTS.includes(role)) {
                setExams(results[1].data || []);
                setCommunications(results[2].data || []);
            }
        } catch {
            toast.error("No se pudo cargar el calendario");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [role]);

    const eventsByDay = useMemo(() => {
        const map = {};
        function push(key, event) {
            if (!key) return;
            if (!map[key]) map[key] = [];
            map[key].push(event);
        }

        assignments.forEach((a) => {
            if (!a.due_date) return;
            push(rawDateKey(a.due_date), {
                type: "tarea",
                title: a.title,
                subtitle: a.lesson_title,
                status: a.status,
                time: rawTime(a.due_date),
                lesson_id: a.lesson_id,
            });
        });

        exams.forEach((ex) => {
            if (!ex.due_date) return;
            push(rawDateKey(ex.due_date), {
                type: "examen",
                title: ex.title,
                subtitle: ex.subject,
                time: rawTime(ex.due_date),
            });
        });

        communications.forEach((c) => {
            push(localDateKey(new Date(c.created_at)), {
                type: "comunicado",
                title: c.title,
                subtitle: c.sender_name,
            });
        });

        reminders.forEach((r) => {
            push(rawDateKey(r.remind_at), {
                type: "recordatorio",
                title: r.title,
                subtitle: r.description,
                time: rawTime(r.remind_at),
                reminder_id: r.id,
                reminder_data: r,
            });
        });

        return map;
    }, [assignments, exams, communications, reminders]);

    const year = cursor.getFullYear();
    const month = cursor.getMonth();

    const cells = useMemo(() => {
        const firstOfMonth = new Date(year, month, 1);
        const startOffset = firstOfMonth.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
        const result = [];
        for (let i = 0; i < totalCells; i++) {
            const dayNum = i - startOffset + 1;
            result.push(dayNum < 1 || dayNum > daysInMonth ? null : new Date(year, month, dayNum));
        }
        return result;
    }, [year, month]);

    function goPrev() { setCursor(new Date(year, month - 1, 1)); }
    function goNext() { setCursor(new Date(year, month + 1, 1)); }
    function goToday() {
        const n = new Date();
        setCursor(new Date(n.getFullYear(), n.getMonth(), 1));
        setSelectedDay(todayKey());
    }

    async function deleteReminder(reminderId) {
        try {
            await api.delete(`/reminders/${reminderId}`);
            toast.success("Recordatorio eliminado");
            load();
        } catch {
            toast.error("Error eliminando recordatorio");
        }
    }

    const selectedEvents = eventsByDay[selectedDay] || [];
    const selectedLabel = selectedDay
        ? new Date(`${selectedDay}T00:00:00`).toLocaleDateString("es-PE", {
              weekday: "long", day: "numeric", month: "long",
          })
        : "Selecciona un día";

    const canCreateReminders = CAN_CREATE_REMINDERS.includes(role);

    return (
        <div className="w-full h-full p-6 text-edu-text box-border">
            <div className="flex flex-row gap-6 w-full max-w-[1400px] mx-auto">

                {/* Panel izquierdo: grilla */}
                <div className="flex-[3] flex flex-col min-w-0">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold capitalize">
                            {MONTH_NAMES[month]} {year}
                        </h2>
                        <div className="flex items-center gap-2">
                            {canCreateReminders && (
                                <button
                                    onClick={() => { setPrefillDate(selectedDay); setEditingReminder(null); setVoiceMode(false); setShowReminderModal(true); }}
                                    className="px-3 py-2 text-xs rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 inline-flex items-center gap-1.5 transition-colors"
                                >
                                    <Plus size={13} /> Recordatorio
                                </button>
                            )}
                            {canCreateReminders && (
                                <button
                                    onClick={() => { setPrefillDate(selectedDay); setEditingReminder(null); setVoiceMode(true); setShowReminderModal(true); }}
                                    className="px-3 py-2 text-xs rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 inline-flex items-center gap-1.5 transition-colors"
                                    title="Dicta tu recordatorio con la voz"
                                >
                                    <Mic size={13} /> Por voz
                                </button>
                            )}
                            <button onClick={goPrev} className="edu-btn-primary p-2" aria-label="Mes anterior">
                                <ChevronLeft size={20} />
                            </button>
                            <button onClick={goNext} className="edu-btn-primary p-2" aria-label="Mes siguiente">
                                <ChevronRight size={20} />
                            </button>
                            <button onClick={goToday} className="px-3 py-2 text-xs rounded-lg border border-edu hover:bg-edu-soft">
                                Hoy
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <p className="text-edu-muted text-sm">Cargando…</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-7 gap-px bg-edu-muted/20 border border-edu-muted/20 rounded-xl overflow-hidden shadow-sm">
                                {WEEKDAYS.map((d) => (
                                    <div key={d} className="bg-edu-soft py-3 text-center text-xs font-bold uppercase text-edu-muted">
                                        {d}
                                    </div>
                                ))}
                                {cells.map((date, idx) => {
                                    if (!date) return <div key={`empty-${idx}`} className="bg-edu-bg min-h-[90px]" />;
                                    const key = localDateKey(date);
                                    const isToday = key === todayKey();
                                    const isSelected = key === selectedDay;
                                    const dayEvents = eventsByDay[key] || [];
                                    return (
                                        <div
                                            key={`day-${key}`}
                                            onClick={() => setSelectedDay(key)}
                                            className={`min-h-[90px] p-2 bg-edu-bg transition-all cursor-pointer hover:bg-edu-soft/50 flex flex-col
                                                ${isSelected ? "bg-edu-soft/30" : ""}
                                                ${isToday ? "border-t-4 border-t-edu-gold" : "border-t-4 border-t-transparent"}`}
                                        >
                                            <div className="flex items-center gap-1 flex-wrap">
                                                <span className={`text-sm font-semibold ${isToday ? "text-edu-gold" : "text-edu-text"}`}>
                                                    {date.getDate()}
                                                </span>
                                                {dayEvents.slice(0, 3).map((ev, i) => {
                                                    const cfg = EVENT_CONFIG[ev.type];
                                                    const dotClass = ev.type === "tarea" ? cfg.dot(ev.status) : cfg.dot();
                                                    return <div key={i} className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />;
                                                })}
                                                {dayEvents.length > 3 && (
                                                    <span className="text-[9px] text-edu-muted">+{dayEvents.length - 3}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Leyenda */}
                            <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-edu-muted">
                                {SEES_COURSE_EVENTS.includes(role) && <>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Tarea pendiente
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Tarea entregada
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Examen
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-slate-500 inline-block" /> Comunicado
                                    </span>
                                </>}
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" /> Recordatorio
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Panel derecho: detalle del día */}
                <aside className="flex-[1] min-w-[280px]">
                    <div className="edu-card p-6 h-full overflow-y-auto">
                        <div className="flex items-center justify-between mb-5 border-b border-edu-muted/20 pb-3">
                            <h3 className="text-base font-bold capitalize">{selectedLabel}</h3>
                            {canCreateReminders && (
                                <button
                                    onClick={() => { setPrefillDate(selectedDay); setEditingReminder(null); setVoiceMode(false); setShowReminderModal(true); }}
                                    className="p-1.5 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
                                    title="Agregar recordatorio"
                                >
                                    <Plus size={14} />
                                </button>
                            )}
                        </div>
                        {selectedEvents.length === 0 ? (
                            <p className="text-edu-muted text-sm italic text-center mt-10">
                                No hay eventos para este día.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {selectedEvents.map((ev, i) => (
                                    <EventRow
                                        key={i}
                                        event={ev}
                                        role={role}
                                        onEditReminder={(r) => { setEditingReminder(r); setShowReminderModal(true); }}
                                        onDeleteReminder={deleteReminder}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            {/* Modal de recordatorio */}
            {showReminderModal && (
                <ReminderModal
                    reminder={editingReminder}
                    prefillDate={prefillDate}
                    autoRecord={voiceMode}
                    onClose={() => { setShowReminderModal(false); setEditingReminder(null); setVoiceMode(false); }}
                    onSaved={() => { setShowReminderModal(false); setEditingReminder(null); setVoiceMode(false); load(); }}
                />
            )}
        </div>
    );
}

// ─── EventRow ───
function EventRow({ event, role, onEditReminder, onDeleteReminder }) {
    const navigate = useNavigate();
    const cfg = EVENT_CONFIG[event.type];
    const Icon = cfg.icon;
    const colorClass = event.type === "tarea"
        ? (event.status === "pending" ? cfg.pendingColor : cfg.submittedColor)
        : cfg.color;
    const label = event.type === "tarea" ? cfg.label(event.status) : cfg.label();

    const isNavigable = event.type === "tarea" && event.lesson_id;
    const isReminder = event.type === "recordatorio";

    function handleClick() {
        if (isNavigable) {
            navigate(`/alumno/lecciones/${event.lesson_id}`);
        }
    }

    return (
        <div
            onClick={!isReminder ? handleClick : undefined}
            className={`p-3 rounded-xl border flex items-start gap-2.5 ${colorClass}
                ${isNavigable ? "cursor-pointer hover:brightness-110 hover:scale-[1.01] transition-all" : ""}
            `}
        >
            <Icon size={16} className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
                    {label}{event.time ? ` · ${event.time}` : ""}
                </p>
                <p className="text-sm font-medium truncate">{event.title}</p>
                {event.subtitle && (
                    <p className="text-xs opacity-60 truncate">{event.subtitle}</p>
                )}
                {isNavigable && (
                    <p className="text-[10px] mt-1 opacity-50">Toca para ir a la lección →</p>
                )}
            </div>
            {/* Acciones de recordatorio */}
            {isReminder && (
                <div className="flex gap-1 flex-shrink-0">
                    <button
                        onClick={() => onEditReminder(event.reminder_data)}
                        className="p-1 rounded hover:bg-purple-500/20 transition-colors"
                        title="Editar"
                    >
                        <Edit2 size={12} />
                    </button>
                    <button
                        onClick={() => onDeleteReminder(event.reminder_id)}
                        className="p-1 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                        title="Eliminar"
                    >
                        <Trash2 size={12} />
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── ReminderModal ───
function ReminderModal({ reminder, prefillDate, autoRecord, onClose, onSaved }) {
    const isEdit = !!reminder;

    // Para edición: conservar fecha y hora del valor guardado
    const initialDateTime = reminder?.remind_at
        ? reminder.remind_at.length > 10
            ? reminder.remind_at.slice(0, 16)   // "2026-06-25T14:30"
            : reminder.remind_at + "T00:00"      // solo fecha → agregar hora 00:00
        : prefillDate
            ? prefillDate + "T00:00"
            : "";

    const [form, setForm] = useState({
        title: reminder?.title || "",
        description: reminder?.description || "",
        remind_at: initialDateTime,
    });
    const [saving, setSaving] = useState(false);

    // ── dictado por voz: graba, manda el audio al backend y rellena el formulario ──
    const [voiceState, setVoiceState] = useState("idle"); // idle | recording | processing
    const [voiceSeconds, setVoiceSeconds] = useState(0);
    const [voiceNote, setVoiceNote] = useState(null); // { transcripcion, horaAsumida }
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const mimeRef = useRef("");
    const timerRef = useRef(null);
    const canceledRef = useRef(false);

    function releaseMic() {
        clearInterval(timerRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    }

    async function startVoice() {
        if (voiceState !== "idle") return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            toast.error("Tu navegador no permite grabar audio. Escribe el recordatorio a mano.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickAudioMimeType();
            mimeRef.current = mimeType || "";
            const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            recorderRef.current = recorder;
            chunksRef.current = [];
            canceledRef.current = false;
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            recorder.onstop = async () => {
                releaseMic();
                if (canceledRef.current) return;
                const blob = new Blob(chunksRef.current, { type: mimeRef.current || "audio/webm" });
                await sendVoice(blob);
            };
            recorder.start();
            setVoiceNote(null);
            setVoiceSeconds(0);
            setVoiceState("recording");
            timerRef.current = setInterval(() => {
                setVoiceSeconds((n) => {
                    if (n + 1 >= MAX_VOICE_SECONDS) { stopVoice(); }
                    return n + 1;
                });
            }, 1000);
        } catch (e) {
            releaseMic();
            toast.error("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
        }
    }

    function stopVoice() {
        clearInterval(timerRef.current);
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive") return;
        setVoiceState("processing");
        recorder.stop();
    }

    async function sendVoice(blob) {
        try {
            const fd = new FormData();
            const ext = (mimeRef.current || "audio/webm").split("/")[1]?.split(";")[0] || "webm";
            fd.append("file", blob, `recordatorio.${ext}`);
            try { fd.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone); } catch (_) { /* usa la zona por defecto */ }
            const { data } = await api.post("/reminders/from-audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((prev) => ({
                title: data.title || prev.title,
                description: data.description || prev.description,
                remind_at: data.remind_at || prev.remind_at,
            }));
            setVoiceNote({ transcripcion: data.transcripcion, horaAsumida: !!data.hora_asumida, sinFecha: !data.remind_at });
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo procesar el audio");
        } finally {
            setVoiceState("idle");
        }
    }

    // Si abrieron el modal con "Por voz", empieza a grabar de inmediato.
    useEffect(() => {
        if (autoRecord && !isEdit) startVoice();
        return () => {
            canceledRef.current = true;
            const r = recorderRef.current;
            if (r && r.state !== "inactive") { try { r.stop(); } catch (_) { /* ya detenido */ } }
            releaseMic();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function save() {
        if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
        if (!form.remind_at) { toast.error("Selecciona fecha y hora"); return; }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/reminders/${reminder.id}`, form);
                toast.success("Recordatorio actualizado");
            } else {
                await api.post("/reminders", form);
                toast.success("Recordatorio creado");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando recordatorio");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold inline-flex items-center gap-2">
                        <Bell size={18} className="text-purple-400" />
                        {isEdit ? "Editar recordatorio" : "Nuevo recordatorio"}
                    </h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-3">
                        {voiceState === "idle" && (
                            <button onClick={startVoice} className="inline-flex items-center gap-2 text-sm text-purple-300 hover:text-purple-200">
                                <Mic size={16} /> Dictar con la voz
                            </button>
                        )}
                        {voiceState === "recording" && (
                            <div className="flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-2 text-sm text-red-300">
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Escuchando… {voiceSeconds}s
                                </span>
                                <button onClick={stopVoice} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300">
                                    <Square size={12} /> Terminar
                                </button>
                            </div>
                        )}
                        {voiceState === "processing" && (
                            <span className="inline-flex items-center gap-2 text-sm text-purple-300">
                                <Loader2 size={16} className="animate-spin" /> Entendiendo tu audio…
                            </span>
                        )}
                        <p className="text-[11px] text-edu-muted mt-1.5">
                            Ejemplo: «Reunión de padres el viernes a las 3 de la tarde en el auditorio».
                        </p>
                        {voiceNote && (
                            <div className="mt-2 text-xs text-edu-body space-y-1">
                                {voiceNote.transcripcion && <p>Escuché: «{voiceNote.transcripcion}»</p>}
                                {voiceNote.sinFecha && <p className="text-amber-400">No entendí la fecha: elígela abajo.</p>}
                                {voiceNote.horaAsumida && !voiceNote.sinFecha && <p className="text-amber-400">No dijiste la hora, puse las 08:00. Ajústala si hace falta.</p>}
                                <p className="text-edu-muted">Revisa los datos antes de guardar.</p>
                            </div>
                        )}
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Título *</span>
                        <input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="ej: Reunión de padres, Preparar examen…"
                            className="edu-input w-full"
                        />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Descripción (opcional)</span>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Detalles adicionales…"
                            rows={2}
                            className="edu-input w-full"
                        />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Fecha y hora *</span>
                        <input
                            type="datetime-local"
                            value={form.remind_at}
                            onChange={(e) => setForm({ ...form, remind_at: e.target.value })}
                            className="edu-input w-full"
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button
                            onClick={save}
                            disabled={saving || voiceState !== "idle" || !form.title.trim() || !form.remind_at}
                            className="edu-btn-primary px-4 py-2 text-sm disabled:opacity-50"
                        >
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
